import {
  PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITH_PREVENT_PLANT,
  PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITHOUT_FALLOW,
  PRIORITY_CASH_CROPS,
  RDCashCropSpecies,
  ResourceDetail,
} from '@cibo/core'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import {
  Autocomplete,
  Button,
  IconButton,
  Paper,
  Popover,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha, Stack } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TraitCellEditorProps } from '../types'

const answerFromDetail = (detail?: ResourceDetail) => {
  return {
    crop: detail?.result.preventPlant ? 'preventPlant' : detail?.result || '',
    preventPlant: detail?.input?.preventPlant,
  }
}

const optionTest = (option: string, value: string) => (value === '' ? false : option === value)

export const CashCropCellEditor = ({
  traitId,
  year,
  params,
  saveDetail,
  removeDetail,
}: TraitCellEditorProps) => {
  const { palette } = useTheme()
  const { t: cropT } = useTranslation('@cibo/ui/cropNames')
  const { t: optionsT } = useTranslation('@cibo/landmanager/CashCropEditor')
  const detail = params.row.findDetail({ traitId, year })
  const [answer, setAnswer] = useState(answerFromDetail(detail))
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const autocompleteRef = useRef<HTMLDivElement | null>(null)
  const open = Boolean(anchorEl)

  useEffect(() => {
    setAnswer(answerFromDetail(detail))
  }, [detail])

  const removeCashCrop = () =>
    removeDetail({
      resourceId: params.row.resourceId,
      traitId,
      year,
    })

  const handleChangeCrop = (value: null | { crop: string; preventPlant: boolean }) => {
    setAnswer(value || { crop: '', preventPlant: false })

    if (value === null) {
      removeCashCrop()
    } else if (value.preventPlant) {
      saveDetail({
        resourceId: params.row.resourceId,
        details: [{ traitId, year, input: value }],
      })
    } else {
      saveDetail({
        resourceId: params.row.resourceId,
        details: [{ traitId, year, result: value.crop }],
      })
    }
  }
  const focusAutocomplete = () =>
    (
      autocompleteRef.current?.getElementsByClassName('MuiInputBase-input')[0] as HTMLInputElement
    )?.focus()

  const onClickAdd = (event: any) => {
    setAnchorEl(event.currentTarget)
    setTimeout(focusAutocomplete, 150)
  }

  return (
    <>
      <Stack direction="row" alignItems="center" width="100%">
        {!answer.crop ? (
          <Button
            sx={{ width: '100%' }}
            onClick={onClickAdd}
            data-testid="add_cash_crop_button"
            aria-label={
              optionsT('addLabel', {
                trait: optionsT(traitId),
                field: params.row.name,
                year,
                context: !!year && 'year',
              }) as string
            }
          >
            <AddIcon />
          </Button>
        ) : (
          <Button
            onClick={onClickAdd}
            aria-label={
              optionsT('editLabel', {
                trait: optionsT(traitId),
                field: params.row.name,
                year,
                context: !!year && 'year',
              }) as string
            }
            sx={{
              minWidth: 0,
              justifyContent: 'center',
              '.MuiSvgIcon-root': { fontSize: '16px' },
              '.MuiButton-endIcon': {
                ml: 2,
                width: '18px',
                height: '18px',
                color: alpha(palette.text.secondary, 0.15),
              },
              '&:hover': {
                '.MuiButton-endIcon': {
                  color: palette.text.secondary,
                },
              },
            }}
            endIcon={<EditIcon />}
          >
            {answer.preventPlant ? (
              <Stack alignItems="flex-start" spacing={-0.5} sx={{ textOverflow: 'ellipsis' }}>
                <Typography>{cropT('preventPlant')}</Typography>
                <Typography variant="caption">{cropT(answer.crop)}</Typography>
              </Stack>
            ) : (
              <Typography>{cropT(answer.crop)}</Typography>
            )}
          </Button>
        )}
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null)
        }}
      >
        <Paper sx={{ p: 2, minWidth: 300 }}>
          {answer.preventPlant && (
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography sx={{ mb: 1 }}>{optionsT('preventedPlanting')}</Typography>
              <IconButton onClick={removeCashCrop}>
                <ClearIcon sx={{ fontSize: 'small' }} />
              </IconButton>
            </Stack>
          )}
          <Autocomplete
            ref={autocompleteRef}
            openOnFocus
            /* This prop is to document having tried to suppress the warning.
             * The warning still shows - probably a mui bug.
             */
            isOptionEqualToValue={optionTest}
            data-testid="cover_crop_editor"
            fullWidth
            disableClearable
            autoComplete
            groupBy={c =>
              PRIORITY_CASH_CROPS.includes(c as RDCashCropSpecies)
                ? optionsT('suggested')
                : optionsT('all')
            }
            options={
              answer.preventPlant
                ? PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITHOUT_FALLOW
                : PRIORITIZED_DISPLAYABLE_CASH_CROPS_WITH_PREVENT_PLANT
            }
            getOptionLabel={cropT}
            renderInput={params => (
              <TextField
                sx={{ marginRight: 1 }}
                label={answer.preventPlant && optionsT('intendedCrop')}
                {...params}
              />
            )}
            value={answer.crop}
            onChange={(event: any, newValue: string) => {
              if (newValue === 'preventPlant') {
                setAnswer({ crop: '', preventPlant: true })
                focusAutocomplete()
                return
              } else if (answer.preventPlant && newValue) {
                handleChangeCrop({ crop: newValue, preventPlant: true })
                return
              }
              handleChangeCrop({ crop: newValue, preventPlant: false })
            }}
          />
        </Paper>
      </Popover>
    </>
  )
}
